import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toggleModalOpen, toggleBulkScheduleModalOpen, setScheduleCreated, setIsBulkSchedule, resetScheduleForm, clearEditingSchedule } from "../../store/schedulingReducer";
import ScheduleInterviewModal from "./ScheduleInterview/ScheduleInterviewModal";
import BulkScheduleModal from "./ScheduleInterview/BulkScheduleModal";
import { Button } from "@material-tailwind/react";
import StatsCard from "../Card/StatsCard";
import { getScheduleButtonData, VALID_STATUSES } from "../constants/Constants";
import SvgMapper from "../features/common/SvgMapper";
import {ReactComponent as BulkUploadLogo} from "../../assets/icons/BulkUploadLogo.svg";

import ScheduleTable from "./ScheduleTable";
import "./Style.css";
import { getScheduleInterviews, getMetricsScheduleInterviews } from "../../application/services/scheduleTabService";
import useDebounce from "../hooks/useDebounce";


const ScheduleTab = () => {
  const dispatch = useDispatch();
  const { isModalOpen, isBulkScheduleModalOpen, scheduleCreated, isBulkSchedule } = useSelector((state) => state.scheduling);
  const handleToggleModal = () => {
    dispatch(clearEditingSchedule());
    dispatch(resetScheduleForm());
    dispatch(toggleModalOpen(true));
  };
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isProcessing, setIsProcessing] = useState(false);
  const [schedulingMetrics, setSchedulingMetrics] = useState(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 1000);
  const debouncedStatusFilter = useDebounce(statusFilter, 1000);

  const isInitialMount = useRef(true);
  const hasInitialFetched = useRef(false);
  const loadingRef = useRef(false);
  const isFetchingRef = useRef(false);
  const scrollRef = useRef(null);

  const pageRef = useRef(1);

  const fetchSchedulingMetrics = useCallback(async () => {
    try {
      const data = await getMetricsScheduleInterviews();
      setSchedulingMetrics(data);
    } catch (err) {
      console.error("Error fetching scheduling metrics:", err);
    }
  }, []);

  useEffect(() => {
    fetchSchedulingMetrics();
  }, [fetchSchedulingMetrics]);

  const fetchScheduleInterviews = useCallback(async (filters = {}, forceRefresh = false, append = false) => {
    if (!forceRefresh && !append && (loadingRef.current || isFetchingRef.current)) {
      return;
    }

    if (append && (loadingRef.current || isFetchingRef.current )) {
      return;
    }

    try {
      loadingRef.current = true;
      isFetchingRef.current = true;
      if (!append) {
        setLoading(true);
      }
      
      const currentPage = append ? pageRef.current : 1;
      const params = {
        page: currentPage,
        pageSize: 20,
      };

      if (filters.status && filters.status !== "all") {
        params.status = filters.status;
      }
      if (filters.student_name) {
        params.student_name = filters.student_name;
      }
      if (filters.email) {
        params.email = filters.email;
      }

      const response = await getScheduleInterviews(params);
      const newData = response?.data || [];
      
      if (append) {
        setScheduleData(prev => [...prev, ...newData]);
        pageRef.current = currentPage + 1;
      } else {
        setScheduleData(newData);
        pageRef.current = 2;
      }
      
      setError(null);
    } catch (err) {
      console.error("Error fetching schedule interviews:", err);
      const errorMessage = err?.response?.data?.message || err?.message || "";
      const errorLower = errorMessage.toLowerCase();
     
      const hasSearchFilter = filters.student_name || filters.email;
      const isSearchError = hasSearchFilter && 
                           errorLower.includes("column") && 
                           (errorLower.includes("does not exist") || 
                            errorLower.includes("not found"));
      
      if (isSearchError) {
        setError(null);
        if (!append) {
          setScheduleData([]);
        }
      } else {
    
        if (err?.response?.status >= 500 || 
            errorLower.includes("network") || 
            errorLower.includes("timeout") ||
            errorLower.includes("failed to fetch")) {
          setError("Unable to fetch schedule interviews. Please try again or contact support.");
        } else if (err?.response?.status >= 400 && err?.response?.status < 500) {
          setError("Unable to load schedule interviews. Please try again.");
        } else {
          setError("Unable to load schedule interviews. Please try again.");
        }
        if (!append) {
          setScheduleData([]);
        }
      }
    } finally {
      loadingRef.current = false;
      isFetchingRef.current = false;
      if (!append) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!hasInitialFetched.current) {
      hasInitialFetched.current = true;
      fetchScheduleInterviews();
    }
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (!hasInitialFetched.current) {
      return;
    }

    const filters = {};

    let effectiveStatus = debouncedStatusFilter;
    let effectiveSearchQuery = debouncedSearchQuery;

    if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
      const trimmedQuery = debouncedSearchQuery.trim().toLowerCase();
      if (VALID_STATUSES.includes(trimmedQuery)) {
        effectiveStatus = trimmedQuery;
        effectiveSearchQuery = ""; 
      }
    }

    const trimmedSearchQuery = effectiveSearchQuery?.trim() || "";
    const hasValidSearch = trimmedSearchQuery.length >= 3;
    const hasStatusFilter = effectiveStatus && effectiveStatus !== "all";
    
    if (!hasValidSearch && !hasStatusFilter && trimmedSearchQuery.length > 0) {
      return;
    }

    if (hasStatusFilter) {
      filters.status = effectiveStatus;
    }

    if (hasValidSearch) {
      const trimmedSearch = trimmedSearchQuery;
      if (trimmedSearch.includes("@")) {
        filters.email = trimmedSearch;
      } else {
        filters.student_name = trimmedSearch;
      }
    }

    pageRef.current = 1;
    fetchScheduleInterviews(filters, false, false);
  }, [debouncedSearchQuery, debouncedStatusFilter, fetchScheduleInterviews]);

  const handleScroll = useCallback(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isNearBottom && !loading && !loadingRef.current && !isFetchingRef.current) {
      const filters = {};
      const effectiveStatus = statusFilter !== "all" ? statusFilter : null;
      const effectiveSearchQuery = searchQuery.trim();

      if (effectiveStatus) {
        filters.status = effectiveStatus;
      }

      if (effectiveSearchQuery && effectiveSearchQuery.trim().length >= 3) {
        const trimmedSearch = effectiveSearchQuery.trim();
        if (trimmedSearch.includes("@")) {
          filters.email = trimmedSearch;
        } else {
          filters.student_name = trimmedSearch;
        }
      }

      fetchScheduleInterviews(filters, false, true);
    }
  }, [loading, statusFilter, searchQuery, fetchScheduleInterviews]);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  useEffect(() => {
    if (scheduleCreated && !isBulkSchedule) {
      const filters = {};
      const effectiveStatus = statusFilter !== "all" ? statusFilter : null;
      const effectiveSearchQuery = searchQuery.trim();

      if (effectiveStatus) {
        filters.status = effectiveStatus;
      }

      if (effectiveSearchQuery && effectiveSearchQuery.trim().length >= 3) {
        const trimmedSearch = effectiveSearchQuery.trim();
        if (trimmedSearch.includes("@")) {
          filters.email = trimmedSearch;
        } else {
          filters.student_name = trimmedSearch;
        }
      }

      pageRef.current = 1;

      setTimeout(() => {
        fetchScheduleInterviews(filters, true, false);
        fetchSchedulingMetrics();
        dispatch(setScheduleCreated(false));
      }, 100);
    }
  }, [scheduleCreated, statusFilter, searchQuery, fetchScheduleInterviews, fetchSchedulingMetrics, dispatch, isBulkSchedule]);

  useEffect(() => {
    if (scheduleCreated && isBulkSchedule) {
      setIsProcessing(true);
      dispatch(setScheduleCreated(false));
      dispatch(setIsBulkSchedule(false));
      dispatch(resetScheduleForm());
      
      setTimeout(() => {
        const filters = {};
        const effectiveStatus = statusFilter !== "all" ? statusFilter : null;
        const effectiveSearchQuery = searchQuery.trim();

        if (effectiveStatus) {
          filters.status = effectiveStatus;
        }

        if (effectiveSearchQuery && effectiveSearchQuery.trim().length >= 3) {
          const trimmedSearch = effectiveSearchQuery.trim();
          if (trimmedSearch.includes("@")) {
            filters.email = trimmedSearch;
          } else {
            filters.student_name = trimmedSearch;
          }
        }

        pageRef.current = 1;
        fetchScheduleInterviews(filters, true, false).then(() => {
          fetchSchedulingMetrics();
          setIsProcessing(false);
        }).catch((error) => {
          console.error("Error refreshing schedule list:", error);
          setIsProcessing(false);
        });
      }, 300000); 
    }
  }, [scheduleCreated, isBulkSchedule, statusFilter, searchQuery, fetchScheduleInterviews, fetchSchedulingMetrics, dispatch]);

  const onBulkSchedule = () => {
    dispatch(toggleBulkScheduleModalOpen(true));
  };
  
  return (
    <>
      {isProcessing && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
          ⏳ Processing uploaded data… This may take up to 5 minutes. The table will refresh automatically once ready.
        </div>
      )}
      <div className="schedule-tab-header">
        <div className="schedule-tab-header-left">
          <h2 className="schedule-tab-title">Interview Scheduling</h2>
          <p className="schedule-tab-subtitle">
            Schedule and manage student interviews
          </p>
        </div>
        <div className="schedule-tab-header-right">
          <Button
            onClick={onBulkSchedule}
            className="schedule-tab-button bg-gray-600 hover:bg-gray-700 cursor-pointer"
          >
            <BulkUploadLogo className="w-4 h-4" />
            <span>Bulk Schedule</span>
          </Button>
          {getScheduleButtonData(SvgMapper).map((btn, index) => (
            <Button
              key={index}
              onClick={btn.label === "Schedule Interview" ? handleToggleModal : null}
              className={`schedule-tab-button ${btn.color}`}
            >
              {btn.icon}
              <span>{btn.label}</span>
            </Button>
          ))}
        </div>
      </div>
      <StatsCard metrics={schedulingMetrics} />
      <div className="schedule-tab-container">
        <div className="schedule-tab-content-header">
          <h3 className="schedule-tab-content-title">Schedule Interviews</h3>
        </div>
        <div className="schedule-tab-content">
        <ScheduleTable 
            scheduleData={scheduleData} 
            loading={loading} 
            error={error}
            searchQuery={searchQuery}
            statusFilter={statusFilter}
            onSearchChange={setSearchQuery}
            onStatusChange={setStatusFilter}
            scrollRef={scrollRef}
          />
        </div>
      </div>
      {isModalOpen && <ScheduleInterviewModal />}
      {isBulkScheduleModalOpen && <BulkScheduleModal />}
    </>
  );
};

export default ScheduleTab;
